const MIN_VERIFIER_LENGTH = 43;
const MAX_VERIFIER_LENGTH = 128;
const CODE_VERIFIER_CHARS =
  "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_";

/** Validate that a code_verifier matches RFC 7636 allowed charset and length */
export function isValidCodeVerifier(verifier: string): boolean {
  return /^[A-Za-z0-9\-_]{43,128}$/.test(verifier);
}

export function generateCodeVerifier(length: number = 64): string {
  const clamped = Math.max(
    MIN_VERIFIER_LENGTH,
    Math.min(MAX_VERIFIER_LENGTH, length),
  );
  const charCount = CODE_VERIFIER_CHARS.length; // 66
  // Rejection-sampling to avoid modulo bias
  const maxByte = 256 - (256 % charCount);
  let out = "";
  const buf = new Uint8Array(128);
  while (out.length < clamped) {
    window.crypto.getRandomValues(buf);
    for (const b of buf) {
      if (b < maxByte) {
        out += CODE_VERIFIER_CHARS.charAt(b % charCount);
        if (out.length === clamped) break;
      }
    }
  }
  // Final safety check
  if (!isValidCodeVerifier(out)) {
    throw new Error("Generated invalid code_verifier");
  }
  return out;
}

export function base64UrlEncode(input: ArrayBuffer | Uint8Array): string {
  const bytes = input instanceof Uint8Array ? input : new Uint8Array(input);
  let binary = "";
  for (const b of bytes) binary += String.fromCharCode(b);
  const base64 = window.btoa(binary);
  return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

export async function computeCodeChallengeS256(
  codeVerifier: string,
): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(codeVerifier);
  const digest = await window.crypto.subtle.digest("SHA-256", data);
  return base64UrlEncode(digest);
}
